import { CloseIcon } from '@/src/components/icon/stroke/CloseIcon';
import { SearchIcon as FlowSearchIcon } from '@/src/components/icon/stroke/SearchIcon';
import { IconButton, InputAdornment, StandardTextFieldProps, SvgIconProps, TextField } from '@mui/material';
import React, {
  forwardRef,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useDeferredValue,
  ComponentType,
} from 'react';
import useDebounce from '../../utils/useDebounce/useDebounce';
import { updateFocusRingStyle } from '@/src/components/focusring/focusring';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export type SearchProps = Omit<StandardTextFieldProps, 'onChange' | 'variant' | 'value' | 'size'> & {
  /**
   *  Defines whether the input is disabled or not
   */
  disableInput?: boolean;
  /**
   * Defines the time (ms) of debounce for the onChange effect
   * When debounce is 0, Search uses useDeferredValue instead.
   *
   * @see useDeferedValue
   * @default 0
   */
  debounce?: number;
  /**
   * Click handler for the cancel button
   */
  onCancel?: () => void;
  /**
   * Click handler for the search button
   *
   * @param value the current value of search input
   */
  onSearch?: (value: string) => void | boolean;
  /**
   * Function that will bed called on every change of value, can be debounced by debounce value
   * If debounce is set to 0, the onChange is being called when deferred value changes
   *
   * @see debounce
   *
   * @param value current value of searchbar
   */
  onChange?: (value: string) => void;
  /**
   * Defines which variant of Searchbar component should be rendered
   * @default solid
   */
  variant?: 'solid' | 'transparent' | 'mapShadow';
  /**
   * Defines default value in the initial render
   * If set along with `value` property the `defaultValue` field takes precedence
   *
   * @see value
   */
  defaultValue?: string;
  /**
   * Overrides TextField value property so it can be string only
   * If set along with `defaultValue` property the `defaultValue` field takes precedence
   *
   * @see defaultValue
   */
  value?: string;
  /**
   * Properties passed to the search icon
   */
  searchIconProps?: SvgIconProps;
  /**
   * Node for search icon
   */
  SearchIconComponent?: ComponentType<SvgIconProps>;
  /**
   * Properties passed to the cancel icon
   */
  cancelIconProps?: SvgIconProps;
  /**
   * Node for search icon
   */
  CancelIconComponent?: ComponentType<SvgIconProps>;
};

export const Search: React.FC<SearchProps> = forwardRef(
  (
    {
      debounce = 0,
      defaultValue = '',
      cancelIconProps = {},
      CancelIconComponent,
      disableInput,
      onCancel,
      onSearch,
      onChange,
      searchIconProps = {},
      SearchIconComponent,
      slotProps,
      sx,
      value = '',
      variant,
      ...rest
    },
    ref,
  ) => {
    const SearchIcon = SearchIconComponent || FlowSearchIcon;
    const CancelIcon = CancelIconComponent || CloseIcon;

    const [search, setSearch] = useState(defaultValue || value);

    const deferredValue = useDeferredValue(search);
    const debouncedValue = useDebounce(search, debounce);

    const { sx: searchIconSx, ...restSearchIconProps } = searchIconProps;
    const { input: slotPropsInput = {}, ...restSlot } = slotProps || {};

    const variantStyle = useMemo(() => {
      switch (variant) {
        case 'transparent':
          return { bgcolor: 'transparent.primary' };
        case 'mapShadow':
          return { bgcolor: 'onSurface.os0', boxShadow: (theme) => theme.flowShadows.l };
        default: // solid
          return { bgcolor: 'surface.primary' };
      }
    }, [variant]);

    // if debounce is set use debouncedValue otherwise use deferredValue as dependency and as value
    useEffect(() => {
      onChange?.(debounce <= 0 ? deferredValue : debouncedValue);
    }, [debounce <= 0 ? deferredValue : debouncedValue]);

    // this handler is assigned to the magnifier button
    const handleSearchIconClick: MouseEventHandler = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();

        onSearch?.(search);
      },
      [search],
    );

    // this handler is assigned to 'X' button
    const handleCancelIconClick = () => {
      setSearch('');
      onCancel?.();
    };

    return (
      <TextField
        onChange={(e) => setSearch(e.target.value)}
        ref={ref}
        sx={{
          position: 'relative',
          borderRadius: '200px',
          border: 1,
          borderColor: rest?.disabled ? 'disabled.primary' : 'onSurface.os500',
          py: '5px',
          pl: '14px',
          pr: '4px',
          mb: 0,
          // focus ring
          '&&&': {
            ...updateFocusRingStyle(1, false, '&:has(.Mui-focused)'),
          },
          ...variantStyle,
          ...sx,
        }}
        {...rest}
        value={search}
        variant="standard"
        slotProps={{
          ...slotPropsInput,
          input: {
            className: `${search !== '' ? 'filled' : ''}`,
            disabled: disableInput || rest?.disabled,
            inputProps: {
              ...('inputProps' in slotPropsInput && { ...slotPropsInput.inputProps }),
              sx: {
                p: 0,
                ml: 1,
                color: 'text.primary',
                '&::-webkit-input-placeholder': {
                  color: 'onSurface.os700',
                },
                '&.Mui-disabled': {
                  '&, &::-webkit-input-placeholder': {
                    color: 'disabled.tertiary',
                  },
                },
                ...('inputProps' in slotPropsInput &&
                  'sx' in slotPropsInput.inputProps && { ...slotPropsInput.inputProps.sx }),
              },
            },
            disableUnderline: true,
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  transition: 'all .4s ease',
                  mr: '0',
                }}
              >
                <IconButton
                  disabled={rest?.disabled}
                  tabIndex={!onSearch ? -1 : 0}
                  onClick={handleSearchIconClick}
                  size="medium"
                  aria-label="Search"
                  sx={{
                    width: '34px',
                    height: '34px',
                    ...updateFocusRingStyle('0px'),
                    ...getInteractiveStyle(
                      {
                        backgroundColor: 'transparent.transparent',
                      },
                      {
                        backgroundColor: 'transparent.transparent',
                      },
                    ),
                  }}
                >
                  <SearchIcon
                    sx={{
                      transition: 'all .5s ease',
                      ...searchIconSx,
                    }}
                    {...restSearchIconProps}
                  />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment
                position="end"
                sx={{
                  opacity: search !== '' ? '1' : '0',
                  transition: 'opacity .3s ease',
                }}
              >
                <IconButton
                  disabled={search === '' || rest?.disabled}
                  onClick={handleCancelIconClick}
                  size="medium"
                  tabIndex={disableInput ? -1 : 0}
                  aria-label="Clear search"
                  sx={{
                    cursor: search !== '' ? 'pointer' : 'default',
                    width: '34px',
                    height: '34px',
                    ...updateFocusRingStyle('0px'),
                    ...getInteractiveStyle(
                      {
                        backgroundColor: 'transparent.transparent',
                      },
                      {
                        backgroundColor: 'transparent.transparent',
                      },
                    ),
                  }}
                >
                  <CancelIcon {...cancelIconProps} />
                </IconButton>
              </InputAdornment>
            ),
          },
          ...restSlot,
        }}
      />
    );
  },
);
